import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteField, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { cn } from "../lib/utils";
import type { FirestoreUserData } from "../types/user";
import { toApplications, type ApiItem } from "../utils/applicationCards";
import { useSyncTimes, useSyncing } from "../utils/gmailSync";
import { seasonSummary } from "../utils/home";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { MELT_BUTTON, MELT_BUTTON_QUIET, MELT_DIALOG } from "./ui/melt";

const REVEAL_STATS: { id: "applied" | "interview" | "offer" | "rejected"; one: string; many: string }[] = [
  { id: "applied", one: "applied", many: "applied" },
  { id: "interview", one: "interview", many: "interviews" },
  { id: "offer", one: "offer", many: "offers" },
  { id: "rejected", one: "rejection", many: "rejections" },
];

/** Counts up from 0 to `to` once, unless the user prefers less motion. */
function useCountUp(to: number, ms = 900) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setN(to);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [to, ms]);
  return n;
}

function RevealStat({ n, one, many, delay }: { n: number; one: string; many: string; delay: number }) {
  const shown = useCountUp(n);
  return (
    <div
      className="flex flex-col items-center rounded-2xl border border-melt-line bg-white/60 px-3 py-4 duration-500 animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className="text-[34px] font-semibold leading-none tracking-[-.03em] text-melt-text1">{shown}</span>
      <span className="mt-1.5 text-[13px] text-melt-text2">{n === 1 ? one : many}</span>
    </div>
  );
}

/**
 * A new user's first look at what Caramel found in their Gmail: how many
 * they applied to and what came back. Shown once (users/{uid}.firstSyncReveal).
 */
function Reveal({
  cards,
  onClose,
  onOpenApplications,
}: {
  cards: ApiItem[];
  onClose: () => void;
  onOpenApplications: () => void;
}) {
  const { counts } = useMemo(() => seasonSummary(cards), [cards]);
  const heard = counts.interview + counts.offer + counts.rejected;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(MELT_DIALOG, "max-w-[460px] px-7 pb-7 pt-6 text-center")}>
        <DialogTitle className="text-[24px] font-semibold tracking-[-.03em] text-melt-text1">
          {heard ? "Here’s what came back." : "Your inbox is all caught up."}
        </DialogTitle>
        <DialogDescription className="mx-auto mt-1.5 max-w-[340px] text-[14.5px] leading-relaxed text-melt-text2">
          {heard
            ? "Caramel sorted your applications into your tracker."
            : "No replies from companies yet. Caramel keeps your applications up to date and moves each one along as replies come in."}
        </DialogDescription>
        {counts.applied > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {REVEAL_STATS.map((stat, i) => (
              <RevealStat key={stat.id} n={counts[stat.id]} one={stat.one} many={stat.many} delay={150 + i * 120} />
            ))}
          </div>
        )}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <button type="button" onClick={onOpenApplications} className={MELT_BUTTON_QUIET}>
            See your applications
          </button>
          <button type="button" onClick={onClose} className={MELT_BUTTON}>
            Let’s go
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Waits for the first sync to finish, wherever in the app they are, then shows the summary */
function WhenSynced() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const syncing = useSyncing();
  const times = useSyncTimes(user);
  const [cards, setCards] = useState<ApiItem[] | null>(null);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      query(collection(db, "users", user.uid, "emails"), where("archiveId", "==", null)),
      (snapshot) => setCards(toApplications(snapshot.docs)),
      (error) => console.error("Could not load applications:", error),
    );
  }, [user]);

  if (!user || !cards || syncing || !times?.lastSyncAt) return null;

  const close = () => {
    updateDoc(doc(db, "users", user.uid), { firstSyncReveal: deleteField() }).catch((e) => console.error(e));
  };
  return (
    <Reveal
      cards={cards}
      onClose={close}
      onOpenApplications={() => {
        close();
        navigate("/dashboard/tracker");
      }}
    />
  );
}

export default function FirstSyncReveal({ userRecord }: { userRecord: FirestoreUserData | null }) {
  return userRecord?.firstSyncReveal === true ? <WhenSynced /> : null;
}
